import { useTranslation } from "react-i18next";

import { useSegmentListCounts } from "../api/useSegmentListCounts";
import { VACANCY_OVERRIDE } from "../constants";
import {
  diffConditions,
  getAffectedText,
  getChangeSummary,
  getEditedHints,
  toRuleChangeChips,
} from "../ruleChanges";
import type { SegmentFormValues } from "../schemas";
import { useDraftBody } from "./useDraftBody";
import { useSegmentOverrides } from "./useSegmentOverrides";

import type { SegmentField } from "@/features/segments/types";
import { useValueFormatter } from "@/utils/useValueFormatter";

type UseBuilderDraftParams = {
  segmentId?: string;
  clientId?: string;
  fields: SegmentField[];
  values: SegmentFormValues;
  savedValues: SegmentFormValues;
  debounceMs: number;
};

export function useBuilderDraft({
  segmentId,
  clientId,
  fields,
  values,
  savedValues,
  debounceMs,
}: UseBuilderDraftParams) {
  const { t } = useTranslation();
  const { format } = useValueFormatter();
  const body = useDraftBody(values, clientId, debounceMs);
  const listCounts = useSegmentListCounts(body);
  const overrides = useSegmentOverrides({
    segmentId,
    draftBody: body,
    overrides: values.vacancyOverrides,
  });

  const changes = segmentId
    ? diffConditions(savedValues.rules, values.rules)
    : [];
  const ruleChanges = toRuleChangeChips(changes, fields, t);
  const editedHints = getEditedHints(changes, fields, t);
  const exclusions = overrides.affected.filter(
    (row) => row.type === VACANCY_OVERRIDE.EXCLUDE,
  ).length;
  const affectedText = getAffectedText(
    { exclusions, inclusions: overrides.affected.length - exclusions },
    t,
    (value) => format(value, "whole"),
  );
  const changeSummary = getChangeSummary(ruleChanges, affectedText, t);

  return {
    body,
    listCounts,
    overrides,
    ruleChanges,
    editedHints,
    changeSummary,
  };
}

export type BuilderDraft = ReturnType<typeof useBuilderDraft>;
